import Async from "components/DesignSystem/AsyncComponents/Async";
import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  BANK_ACCOUNT_STATUS,
  BANK_CONNECTION_PROVIDER,
} from "constants/dataSource";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useDataSourceSegregate } from "hooks/useDataSourceSegregate";
import { useGetAllConnections } from "hooks/useGetAllConnections";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { pluralize } from "utils/pluralize";
import { BillComCard } from "../APandAR/BillComCard";
import {
  AccountBanner,
  AccountCardsSection,
  RevenueCard,
} from "../DataSourcesList";
import { BankConnections } from "../DataSourceView/BankConnections";
import { GustoCard } from "../Gusto/GustoCard";
import { getDisconnectedMessage } from "utils/bankNameAcc";

export const AllAccount = ({
  groupId,
  onMigrationSuccess,
}: {
  groupId: string;
  onMigrationSuccess: () => void;
}) => {
  const entityId = useCurrentEntityId();
  const {
    data: plaidConnections,
    isLoading: bankAccountsLoading,
    isSuccess: banksAccountsLoaded,
  } = useGetEntityBanksQuery(
    {
      groupId: groupId!,
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    {
      entityId: entityId!,
    },
    { skip: !groupId || !entityId }
  );

  const { stripeConnection } = useGetAllConnections({
    groupId: groupId!,
    entityId: entityId!,
  });

  const { totalAccounts, totalBalance, bankConnections, stripeConnectedBank } =
    useDataSourceSegregate(plaidConnections!);

  const apArBankConnections = bankConnections?.filter(
    (connection) =>
      connection.connection_provider === BANK_CONNECTION_PROVIDER.BILL_DOT_COM
  );

  const nonApArBankConnections = bankConnections?.filter(
    (connection) =>
      connection.connection_provider !== BANK_CONNECTION_PROVIDER.BILL_DOT_COM
  );

  return (
    <Async.Root
      isSuccess={banksAccountsLoaded}
      isLoading={bankAccountsLoading}
      isEmpty={false}
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <div className="t-overflow-y-auto t-py-3 t-px-2.5">
          <AccountBanner
            totalBalance={totalBalance}
            totalAccounts={totalAccounts}
          />
          <AccountCardsSection>
            {nonApArBankConnections?.map((connection) => {
              return (
                <BankConnections
                  key={connection.uuid}
                  connection={connection}
                  onMigrationSuccess={onMigrationSuccess}
                />
              );
            })}
            {stripeConnection && (
              <RevenueCard
                stripeConnection={stripeConnection}
                stripeConnectedBank={stripeConnectedBank}
              />
            )}
            {bookkeepingSettings?.gusto_access && (
              <GustoCard
                name="Gusto"
                description="Access Shared"
                tag={
                  <Tag size="small" rounded icon={false} tagType="light_grey">
                    Manually added
                  </Tag>
                }
              />
            )}
            {apArBankConnections?.map((connection) => {
              const accountCount =
                connection.bank_accounts.filter(
                  ({ status }) => status === BANK_ACCOUNT_STATUS.ACTIVE
                )?.length || 0;

              const isConnected = connection.is_connection_available;

              const disconnectedMessage = getDisconnectedMessage(connection);

              const description = (
                <span
                  className={isConnected ? "t-text-green-80" : "t-text-red-70"}
                >
                  {isConnected
                    ? `${pluralize(
                        accountCount,
                        "account",
                        "accounts"
                      )} connected`
                    : disconnectedMessage}
                </span>
              );

              return (
                <BillComCard
                  key={connection.uuid}
                  name={connection.name}
                  description={description}
                  connection={connection}
                />
              );
            })}
          </AccountCardsSection>
        </div>
      </Async.Success>
    </Async.Root>
  );
};
